import { ChevronLeft, ChevronRight, Download, Loader2, Play, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Video } from '../lib/api';
import { galleryStoryStyleOf, type GalleryStyle } from '../lib/galleryCustomisation';
import { handleMediaError, handleMediaLoad, type MediaErrorHandler } from '../lib/mediaRecovery';
import { formatBytes } from '../lib/utils';
import { Button } from './ui/Button';

export function VideoGallery({
  videos,
  placeholderCount = 0,
  isLoading = false,
  loadingFailed = false,
  showDetails = true,
  onDelete,
  deletingVideoId,
  galleryStyle = 'mosaic',
  onDownload,
  onMediaError
}: {
  videos: Video[];
  placeholderCount?: number;
  isLoading?: boolean;
  loadingFailed?: boolean;
  showDetails?: boolean;
  onDelete?: (videoId: string) => void;
  deletingVideoId?: string | null;
  galleryStyle?: GalleryStyle;
  onDownload: (video: Video) => void;
  onMediaError?: MediaErrorHandler;
}) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [loadedPosters, setLoadedPosters] = useState<Set<string>>(() => new Set());
  const reducedMotion = useReducedMotion();
  const activeVideo = activeIndex === null ? null : videos[activeIndex];
  const storyStyle = galleryStoryStyleOf(galleryStyle);
  const slotCount = Math.max(videos.length, Math.max(0, Math.floor(placeholderCount)));
  const postersLoading = videos.some((video) => !loadedPosters.has(posterKey(video)));

  useEffect(() => {
    if (activeIndex === null) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveIndex(null);
      } else if (event.key === 'ArrowLeft') {
        setActiveIndex((current) => (current === null ? null : (current - 1 + videos.length) % videos.length));
      } else if (event.key === 'ArrowRight') {
        setActiveIndex((current) => (current === null ? null : (current + 1) % videos.length));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeIndex, videos.length]);

  if (slotCount === 0) {
    return null;
  }

  const move = (delta: -1 | 1) =>
    setActiveIndex((current) => (current === null ? null : (current + delta + videos.length) % videos.length));

  return (
    <>
      <div
        className="client-gallery-video-layout grid gap-4 md:grid-cols-2"
        data-gallery-style={storyStyle}
        aria-busy={isLoading || (!loadingFailed && postersLoading)}
      >
        {Array.from({ length: slotCount }, (_, index) => {
          const video = videos[index];
          const isPosterLoaded = Boolean(video && loadedPosters.has(posterKey(video)));
          const animatePlaceholder = !reducedMotion && !loadingFailed && (isLoading || Boolean(video && !isPosterLoaded));
          return (
            <article key={`video-slot-${index}`} className="group relative overflow-hidden rounded-sm bg-white shadow-sm ring-1 ring-ink/10">
              <div className="relative aspect-video">
                <span
                  className="client-gallery-video-placeholder absolute inset-0"
                  data-video-placeholder
                  data-animated={animatePlaceholder}
                  aria-hidden="true"
                />
                {video ? (
                  <button
                    type="button"
                    className="group absolute inset-0 z-[1] block h-full w-full overflow-hidden bg-transparent text-left"
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Play ${video.fileName}`}
                    disabled={!isPosterLoaded}
                  >
                    <img
                      src={video.posterUrl}
                      alt=""
                      className={`client-gallery-video-reveal absolute inset-0 h-full w-full object-cover opacity-0 ${
                        reducedMotion ? '' : 'transition duration-300 group-hover:scale-[1.02]'
                      } ${isPosterLoaded ? 'opacity-100' : ''}`}
                      loading="lazy"
                      onLoad={(event) => {
                        setLoadedPosters((current) => {
                          const next = new Set(current);
                          next.add(posterKey(video));
                          return next;
                        });
                        handleMediaLoad(event);
                      }}
                      onError={(event) => handleMediaError(event, onMediaError)}
                    />
                    <span className={`client-gallery-video-reveal absolute inset-0 bg-ink/15 opacity-0 ${
                      reducedMotion ? '' : 'transition duration-300 group-hover:bg-ink/20'
                    } ${isPosterLoaded ? 'opacity-100' : ''}`} />
                    <span
                      className={`client-gallery-video-reveal absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-ink opacity-0 shadow-soft ${
                        reducedMotion ? '' : 'transition-opacity duration-300'
                      } ${isPosterLoaded ? 'opacity-100' : ''}`}
                      data-video-play-control
                    >
                      <Play className="ml-0.5 h-6 w-6 fill-current" />
                    </span>
                  </button>
                ) : null}
              </div>
              {video && (showDetails || onDelete) ? (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-ink/88 via-ink/62 to-transparent p-3 text-white opacity-0 transition duration-200 group-hover:opacity-100 group-focus-within:opacity-100">
                  <div className="pointer-events-auto flex min-h-16 items-end justify-between gap-3">
                    {showDetails ? (
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{video.fileName}</p>
                        <p className="text-xs text-white/80">
                          {formatBytes(video.sizeBytes)}
                          {video.scene ? ` · ${video.scene}` : ' · No scene'}
                        </p>
                      </div>
                    ) : <div />}
                    <div className="flex shrink-0 items-center gap-1">
                      {onDelete ? (
                        <Button
                          variant="secondary"
                          size="icon"
                          onClick={(event) => {
                            event.stopPropagation();
                            onDelete(video.photoId);
                          }}
                          disabled={deletingVideoId === video.photoId}
                          aria-label={deletingVideoId === video.photoId ? `Deleting ${video.fileName}` : `Delete ${video.fileName}`}
                        >
                          {deletingVideoId === video.photoId ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}
            </article>
          );
        })}
      </div>

      {activeVideo ? createPortal((
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/95 p-3" role="dialog" aria-modal="true" aria-label="Video player">
          <button className="absolute inset-0 cursor-default" onClick={() => setActiveIndex(null)} aria-label="Close video player" />
          <div className="relative z-10 flex w-full max-w-6xl flex-col gap-3">
            <div className="flex items-center justify-between gap-3 text-white">
              <div className="min-w-0">
                {showDetails ? <p className="truncate text-sm font-medium">{activeVideo.fileName}</p> : null}
                {videos.length > 1 ? <p className="text-xs text-white/60">{activeIndex! + 1} of {videos.length}</p> : null}
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => onDownload(activeVideo)}>
                  <Download className="h-4 w-4" /> <span>Download original</span>
                </Button>
                <Button variant="secondary" size="icon" onClick={() => setActiveIndex(null)} aria-label="Close video player">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="relative flex items-center justify-center">
              {videos.length > 1 ? (
                <Button variant="secondary" size="icon" className="absolute left-1 z-20" onClick={() => move(-1)} aria-label="Previous video">
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              ) : null}
              <video
                key={activeVideo.photoId}
                src={activeVideo.playbackUrl}
                poster={activeVideo.posterUrl}
                controls
                autoPlay
                playsInline
                className="max-h-[calc(100vh-7rem)] max-w-full rounded-sm bg-black shadow-2xl"
              />
              {videos.length > 1 ? (
                <Button variant="secondary" size="icon" className="absolute right-1 z-20" onClick={() => move(1)} aria-label="Next video">
                  <ChevronRight className="h-5 w-5" />
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      ), document.body) : null}
    </>
  );
}

function posterKey(video: Video): string {
  return `${video.photoId}\u0000${video.posterUrl}`;
}

function useReducedMotion(): boolean {
  const [reducedMotion, setReducedMotion] = useState(() => (
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ));

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return reducedMotion;
}

export function formatDuration(seconds: number): string {
  const totalSeconds = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(totalSeconds / 60);
  const remainder = totalSeconds % 60;
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
}
